import { useCompareUrlWithProjectId } from 'common/hooks/route';
import { KWELLO_ICON } from '../icon/KwelloSvgIcon';
import KwelloIconButton from '../KwelloButton/KwelloIconButton';
import { useDispatch, useSelector } from 'react-redux';
import { PAGINATED_PPT_EXPORT_ROUTES } from 'modules/insights/constants';
import { showPaginatedPptFlyout, showPptFlyout } from 'common/store/slices/export-ppt.slice';
import { useResize } from 'common/hooks/resize';
import {
  ANALYZE_ROUTES,
  PROFILE_CAPTURE_ROUTES,
  REPORTS_ROUTES,
  CAPTURE_ROUTES,
  USER_ROUTES,
  INSIGHTS_ROUTES,
  ALIGNMENT_ROUTES,
} from 'routes/routes.const';
import useFeatureFlags from 'common/hooks/useFeatureFlags';
import { Feature } from 'common/models/page-config.models';
import { selectWidgetsHavePptExports } from 'common/store/slices/layout.slice';
import useApiLoadingState from 'common/hooks/useApiLoadingState';
import { useMemo } from 'react';
import usePowerBiReport from 'common/hooks/usePowerBiReport';

const NO_PPT_EXPORT_PAGES: string[] = [
  USER_ROUTES.root,
  ANALYZE_ROUTES.elsieai,
  ANALYZE_ROUTES.elsieaiV2,
  CAPTURE_ROUTES.root,
  REPORTS_ROUTES.library,
  PROFILE_CAPTURE_ROUTES.root,
  INSIGHTS_ROUTES.compare,
  ALIGNMENT_ROUTES.compare,
  REPORTS_ROUTES.wizard,
  REPORTS_ROUTES.editor,
  REPORTS_ROUTES.catalog,
  REPORTS_ROUTES.configurator,
];

function PowerPointPageExportButton() {
  const dispatch = useDispatch();
  const { compareUrl } = useCompareUrlWithProjectId();
  const { isMobile } = useResize();
  const { hasFeature } = useFeatureFlags();
  const widgetsHavePPTExports = useSelector(selectWidgetsHavePptExports);
  const { chartApiLoadingState } = useApiLoadingState();
  const { isPowerBiReport } = usePowerBiReport();

  const buttonSettings = useMemo(() => {
    if (chartApiLoadingState.isLoading) {
      return {
        isLoading: true,
        isError: false,
        tooltip: 'Loading PowerPoint export...',
      };
    }

    if (chartApiLoadingState.isFailed) {
      return {
        isLoading: false,
        isError: true,
        tooltip: 'Error loading all widgets',
      };
    }

    if (chartApiLoadingState.isEmptyPage) {
      return {
        isLoading: false,
        isError: false,
        tooltip: 'No data available for PowerPoint export',
      };
    }

    return {
      isLoading: false,
      isError: false,
      tooltip: 'Export page to PowerPoint',
    };
  }, [
    chartApiLoadingState.isLoading,
    chartApiLoadingState.isFailed,
    chartApiLoadingState.isEmptyPage,
  ]);

  const onClick = () => {
    if (compareUrl(PAGINATED_PPT_EXPORT_ROUTES, { includes: true })) {
      dispatch(showPaginatedPptFlyout());
    } else {
      dispatch(showPptFlyout());
    }
  };

  if (
    isPowerBiReport ||
    !widgetsHavePPTExports ||
    isMobile ||
    NO_PPT_EXPORT_PAGES.find((exclusion) => compareUrl(exclusion, { includes: true })) ||
    (compareUrl(REPORTS_ROUTES.root, { includes: true }) &&
      !hasFeature(Feature.pptExportSummaryReports)) ||
    !hasFeature(Feature.pptExportSummary)
  ) {
    return null;
  }

  return (
    <KwelloIconButton
      icon={KWELLO_ICON.EXPORT_PPTX_ICON}
      onClick={onClick}
      tooltip={buttonSettings.tooltip}
      tooltipPosition="bottom"
      disabled={buttonSettings.isLoading || chartApiLoadingState.isEmptyPage}
      isLoading={buttonSettings.isLoading}
      isError={buttonSettings.isError}
    />
  );
}

export default PowerPointPageExportButton;
